import { Modal, ModalProps } from "antd-v5";
import useCSSVarCls from "antd-v5/es/config-provider/hooks/useCSSVarCls";
import useModalStyle from "antd-v5/es/modal/style";
import classNames from "classnames";
import React from "react";

import ACButton, { ACButtonProps } from "ac-components/ac-button/ACButton";

import "./ACModal.less";

type ACModalProps = Omit<
  ModalProps,
  | "prefixCls"
  | "footer"
  | "closeIcon"
  | "closable"
  | "okButtonProps"
  | "cancelButtonProps"
  | "getContainer"
> & {
  extras?: React.ReactNode;
  okButtonLoading?: boolean;
  okButtonProps?: Omit<ACButtonProps, "children">;
  cancelButtonProps?: Omit<ACButtonProps, "children">;
};

function ACModal({
  title,
  extras,
  okText = "OK",
  cancelText = "Cancel",
  onOk,
  onCancel,
  okButtonLoading,
  okButtonProps,
  cancelButtonProps,
  ...modalProps
}: ACModalProps) {
  return (
    <Modal
      title={
        <div className="ac-modal-title-container">
          <div className="ac-modal-title-content">{title}</div>
          <div>{extras}</div>
        </div>
      }
      closable={false}
      prefixCls="ac-modal"
      footer={
        <>
          <ACButton type="outline" onClick={onCancel} {...cancelButtonProps}>
            {cancelText}
          </ACButton>
          <ACButton loading={okButtonLoading} onClick={onOk} {...okButtonProps}>
            {okText}
          </ACButton>
        </>
      }
      getContainer={() =>
        (document.getElementsByClassName(
          "app-content-wrapper",
        )[0] as HTMLElement) || document.body
      }
      {...modalProps}
    />
  );
}

export default ACModal;

type ACModalInlineProps = Pick<
  ACModalProps,
  "title" | "children" | "className" | "extras" | "style"
> & {
  footer?: React.ReactNode | React.ReactNode[];
};

export function ACModalInline({
  title,
  extras,
  footer,
  className,
  style,
  children,
}: ACModalInlineProps) {
  const rootCls = useCSSVarCls("ac-modal");
  const [wrapCSSVar, hashId, cssVarCls] = useModalStyle("ac-modal", rootCls);

  return wrapCSSVar(
    <div
      style={style}
      className={classNames(
        "ac-modal-root ac-modal ac-modal-inline",
        hashId,
        cssVarCls,
        className,
      )}
    >
      <div className="ac-modal-content">
        <div className="ac-modal-header">
          <div className="ac-modal-title">
            <div className="ac-modal-title-container">
              <div className="ac-modal-title-content">{title}</div>
              <div>{extras}</div>
            </div>
          </div>
        </div>
        <div className="ac-modal-body">{children}</div>
        {footer ? <div className="ac-modal-footer">{footer}</div> : null}
      </div>
    </div>,
  );
}
